<template>
  <div class="signing-checklist-selected-documents-list-item py-2" :class="{ 'not-signable' : isFileOverPages }" data-locator="selected-file-row" :data-name="file.objectToPost.fileName">
    <ClosdFildersIcon
      :document="file"
      :signed-file="file.signedOnClosd"
    />

    <div class="signing-checklist-selected-documents-list-item__file-data ml-2 mr-4">
      <template v-if="hasToBeUploaded">
        <div class="d-flex justify-space-between mb-1">
          <p class="text-grey-900 mb-0 text--small d-flex">
            {{ file.objectToPost.fileName }}
            <SigningChecklistDocumentWarning :file="file" textInline @update:isFileOverPages="handleFileOverPages"/>
          </p>
          <p v-if="file.size" class="text--x-small mb-0">
            <app-filesize-display :value="file.size" />
          </p>
        </div>
        <v-progress-linear
          :model-value="file.progress"
          height="6"
          color="primary"
          bg-color="grey-500"
          rounded
        />
      </template>
      <template v-else>
        <p class="text-grey-900 mb-0 text--small">
          {{ file.objectToPost.fileName }}
        </p>
        <p v-if="file.size" class="text--x-small mb-0 d-flex">
          <app-filesize-display :value="file.size"/>
          <SigningChecklistDocumentWarning :file="file" textInline @update:isFileOverPages="handleFileOverPages"/>
        </p>
      </template>
    </div>

    <div class="signing-checklist-selected-documents-list-item__actions">
      <app-button
        size="small"
        type="ghost"
        data-locator="replace-button"
        @click="handleSwapFiles"
      >
        {{ $t('common.replace') }}
      </app-button>
      <app-button
        v-if="canDelete"
        size="small"
        type="outlined"
        variant="danger"
        data-locator="delete-button"
        @click="$emit('deleteFile', file)"
      >
        {{ $t('common.delete') }}
      </app-button>
      <template v-if="hasToBeUploaded">
        <font-awesome-icon
          v-if="file.status === 'success'"
          class="signing-checklist-selected-documents-list-item__actions-success"
          :icon="['fad', 'check-circle']"
        />
        <font-awesome-icon
          v-if="file.status === 'error'"
          class="signing-checklist-selected-documents-list-item__actions-error"
          :icon="['fad', 'exclamation-circle']"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue'
import { useStore } from 'vuex'

import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import SigningChecklistDocumentWarning from '@/project/signing-checklist/SigningChecklistDocumentWarning.vue'
import { SigningChecklistFile } from '@/project/views/SigningChecklistAdd.vue'

const props = defineProps({
  file: {
    type: Object as PropType<SigningChecklistFile>,
    required: true,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
})

const hasToBeUploaded = computed(() => !!props.file.objectToPost.fileRaw)

const isFileOverPages = ref(false)
const handleFileOverPages = (value: boolean) => {
  isFileOverPages.value = value
}

const store = useStore()

const handleSwapFiles = () => {
  // envelopeId is null when the file is only in add process
  const envelopeId = props.file?.objectToPost?.envelope?.id ?? null;
  const fileToSwap = {
    objectToPost: props.file.objectToPost,
    envelope: {id:envelopeId, file: props.file},
    title: props.file.objectToPost.fileName,
  }
  store.commit('signingChecklist/SET_SWAP_FILES', {
    ids: [props.file.id],
    steps: [fileToSwap],
  })
}
</script>

<style scoped lang="scss">
.signing-checklist-selected-documents-list-item {
  display: flex;
  align-items: center;
  padding-right: 8px;
  &__file-data {
    flex: 1;
  }

  &__actions {
    display: flex;
    column-gap: 12px;

    svg {
      width: 18px;
      height: 18px;
    }

    &-success,
    &-error {
      --fa-primary-opacity: 1;
      --fa-secondary-opacity: 1;
    }
    &-success {
      --fa-primary-color: #f0f0f0;
      --fa-secondary-color: #4e985f;
    }
    &-error {
      --fa-primary-color: #b99c6b;
      --fa-secondary-color: rgb(245, 230, 178);
    }
  }

  &:not(:last-of-type):not(.not-signable):has(+ :not(.not-signable)) {
    border-bottom: 1px solid rgb(var(--v-theme-grey-300));
  }
  &.not-signable {
    background-color: rgb(var(--v-theme-yellow-50));
    border: 1px solid rgb(var(--v-theme-yellow-500));
    border-radius: 8px;
  }
}
</style>
